'use client'

import { useCallback, useContext, useEffect, useRef } from 'react'

import {
  AdPage,
  AdShape,
  ClickAdEventTarget,
  VanAdEligibleScreen,
  VanPlacementConfigModel,
} from '@marketplace-web/ads/ads-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { ConsentGroup, useIsConsentGroupEnabled } from '@marketplace-web/consent/consent-feature'
import { useUserAgent } from '@marketplace-web/environment/request-context-data'
import { useSystemConfiguration } from '@marketplace-web/shared/system-configuration-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { CountryCode } from '@marketplace-web/country/country-data'

import AdsContext from '../../containers/AdsProvider/AdsContext'
import useFetchVanMultiPlacements from '../useFetchVanMultiPlacements'
import useClickAdEventTracking from '../useClickAdEventTracking'

type AbTestVariant = 'a' | 'b'

type Props = {
  abTestVariant: AbTestVariant
  shouldFetchOffer: boolean
}

const useFetchDigitalLabelOffer = ({ abTestVariant, shouldFetchOffer }: Props) => {
  const hasFetchedRef = useRef(false)

  const isDigitalOfferEnabled = useFeatureSwitch('web_digital_label_offer_advertisement')
  const hasConsentedTargeting = useIsConsentGroupEnabled(ConsentGroup.VanAdvertising)

  const userAgent = useUserAgent()
  const { getAdsPlatform } = useContext(AdsContext)
  const { userCountry: countryCode } = useSystemConfiguration()
  const { trackClickAdEvent } = useClickAdEventTracking()

  const { screen: screenName } = useSession()

  const inboxScreens: Array<string> = Object.values([
    VanAdEligibleScreen.MessageList,
    VanAdEligibleScreen.MessageReply,
    VanAdEligibleScreen.MessageNew,
  ])
  const isInInbox = inboxScreens.includes(screenName)

  const isVanOfferAdEnabled = useFeatureSwitch('web_van_offer_ad')
  const shouldUserSeeAds = useAbTest('sqp_ads')?.variant === 'on'
  const digitalLabelOfferAbTest = useAbTest('van_digital_shipping_label_ad')

  const shouldFetchVanMultiOffer =
    isVanOfferAdEnabled &&
    isInInbox &&
    shouldUserSeeAds &&
    isDigitalOfferEnabled &&
    digitalLabelOfferAbTest?.variant === abTestVariant

  const placementId = `int_web_digital_label_offer_van_${abTestVariant}`

  const { placements, isLoading, fetchVanMultiAdPlacements } = useFetchVanMultiPlacements({
    placementId,
    shape: AdShape.Rectangle,
    consentStatus: hasConsentedTargeting,
    page: AdPage.Messages,
    platform: getAdsPlatform(userAgent),
    countryCode: countryCode as CountryCode,
  })

  const trackBannerOfferClick = useCallback(() => {
    if (!placements[0]) return

    // DSA requrements is to return certain fields empty
    const normalizedPlacement: VanPlacementConfigModel = {
      ...placements[0],
      correlationId: undefined,
      id: null,
      creative: null,
    }

    trackClickAdEvent(normalizedPlacement, ClickAdEventTarget.OpenPromotions)
  }, [trackClickAdEvent, placements])

  useEffect(() => {
    if (!shouldFetchOffer || !shouldFetchVanMultiOffer || hasFetchedRef.current) return

    hasFetchedRef.current = true
    fetchVanMultiAdPlacements()
  }, [fetchVanMultiAdPlacements, shouldFetchOffer, shouldFetchVanMultiOffer])

  const isPending = shouldFetchOffer && shouldFetchVanMultiOffer && !hasFetchedRef.current

  return {
    placements,
    hasOffers: placements.length > 0,
    isLoading: isLoading || isPending,
    trackBannerOfferClick,
  }
}

export default useFetchDigitalLabelOffer
